import { CurrencyInput, Input, formatCurrency } from '@hue/design-system';
import {
  serviceTermsFor,
  type ProfessionalServiceTerms,
  type ServiceOffering,
} from '../../lib/api.js';

/// Preço e duração próprios do profissional nos serviços marcados (ADR 0074, decisão 8). Campo
/// vazio usa o do serviço na unidade.
export function ProfessionalServiceTermsFields({
  services,
  businessUnitId,
  value,
  onChange,
  disabled,
}: {
  services: readonly ServiceOffering[];
  businessUnitId: string | null | undefined;
  value: readonly ProfessionalServiceTerms[];
  onChange: (value: ProfessionalServiceTerms[]) => void;
  disabled?: boolean | undefined;
}) {
  if (!services.length) return null;

  function change(serviceId: string, patch: Partial<ProfessionalServiceTerms>) {
    const current = value.find((terms) => terms.serviceId === serviceId) ?? {
      serviceId,
      priceCents: null,
      durationMinutes: null,
    };
    onChange([...value.filter((terms) => terms.serviceId !== serviceId), { ...current, ...patch }]);
  }

  return (
    <fieldset className="grid gap-3 sm:col-span-2">
      <legend className="mb-1 text-sm font-semibold">
        Preço e duração deste profissional{' '}
        <span className="font-normal text-content-muted">· opcional</span>
      </legend>
      <p className="text-xs leading-5 text-content-muted">
        Em branco, vale o do serviço nesta unidade. Vale para novos agendamentos; o que já foi
        marcado não muda.
      </p>
      <ul className="grid gap-2">
        {services.map((service) => {
          const terms = value.find((item) => item.serviceId === service.id);
          const unit = serviceTermsFor(service, businessUnitId);
          return (
            <li
              key={service.id}
              className="grid gap-3 rounded-xl border border-surface-border px-3 py-3 sm:grid-cols-[minmax(0,1fr)_10rem_9rem] sm:items-end"
            >
              <span className="text-sm font-medium sm:pb-3">{service.displayName}</span>
              <CurrencyInput
                label={`Preço em ${service.displayName}`}
                currency={service.currency}
                value={terms?.priceCents ?? null}
                hint={`Vazio: ${formatCurrency(unit.priceCents, service.currency)}`}
                onValueChange={(priceCents) => change(service.id, { priceCents })}
                disabled={disabled}
              />
              <Input
                label={`Minutos em ${service.displayName}`}
                type="number"
                inputMode="numeric"
                min={5}
                max={1440}
                step={5}
                hint={`Vazio: ${unit.durationMinutes} min`}
                value={terms?.durationMinutes ?? ''}
                onChange={(event) =>
                  change(service.id, {
                    durationMinutes: event.target.value === '' ? null : Number(event.target.value),
                  })
                }
                disabled={disabled}
              />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
